import React, { useState, useEffect, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Lightbulb, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { OnboardingStep } from "./onboarding-types";

interface OnboardingTooltipProps {
  steps: OnboardingStep[];
  isActive: boolean;
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
  className?: string;
}

export function OnboardingTooltip({
  steps,
  isActive,
  currentStep,
  onNext,
  onPrevious,
  onSkip,
  onClose,
  className
}: OnboardingTooltipProps) {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipDirection, setTooltipDirection] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const currentStepData = steps[currentStep];

  useEffect(() => {
    if (!isActive || !currentStepData) {
      setIsVisible(false);
      return;
    }

    const targetElement = document.querySelector(currentStepData.target) as HTMLElement;
    if (!targetElement) {
      console.warn(`Onboarding target not found: ${currentStepData.target}`);
      return;
    }

    const updatePosition = () => {
      const targetRect = targetElement.getBoundingClientRect();
      const tooltipElement = tooltipRef.current;
      if (!tooltipElement) return;

      const tooltipRect = tooltipElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let position = currentStepData.position || 'auto';
      let x = 0;
      let y = 0;

      // Auto-determine position if needed
      if (position === 'auto') {
        const spaceTop = targetRect.top;
        const spaceBottom = viewportHeight - targetRect.bottom;
        const spaceLeft = targetRect.left;
        const spaceRight = viewportWidth - targetRect.right;

        const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
        
        if (maxSpace === spaceTop && spaceTop > 200) {
          position = 'top';
        } else if (maxSpace === spaceBottom && spaceBottom > 200) {
          position = 'bottom';
        } else if (maxSpace === spaceLeft && spaceLeft > 300) {
          position = 'left';
        } else if (maxSpace === spaceRight && spaceRight > 300) {
          position = 'right';
        } else {
          position = 'bottom'; // Default fallback
        }
      }

      // Calculate position based on determined direction
      switch (position) {
        case 'top':
          x = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          y = targetRect.top - tooltipRect.height - 15;
          break;
        case 'bottom':
          x = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          y = targetRect.bottom + 15;
          break;
        case 'left':
          x = targetRect.left - tooltipRect.width - 15;
          y = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          break;
        case 'right':
          x = targetRect.right + 15;
          y = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          break;
      }

      // Ensure tooltip stays within viewport
      x = Math.max(10, Math.min(x, viewportWidth - tooltipRect.width - 10));
      y = Math.max(10, Math.min(y, viewportHeight - tooltipRect.height - 10));

      setTooltipPosition({ x, y });
      setTooltipDirection(position);
    };

    // Add highlight to target element
    targetElement.classList.add('onboarding-highlight');
    if (currentStepData.highlightPulse) {
      targetElement.classList.add('onboarding-pulse');
    }

    // Initial position calculation
    updatePosition();
    
    // Show tooltip with animation
    setTimeout(() => setIsVisible(true), 100);

    // Update position on scroll/resize
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      targetElement.classList.remove('onboarding-highlight', 'onboarding-pulse');
    };
  }, [currentStep, isActive, currentStepData]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsAnimatingOut(false);
        onNext();
      }, 200);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsAnimatingOut(false);
        onPrevious();
      }, 200);
    }
  };

  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose();
      setIsAnimatingOut(false);
    }, 200);
  };

  if (!isActive || !currentStepData || !isVisible) {
    return null;
  }

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300" />
      
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={cn(
          "fixed z-50 transition-all duration-300 ease-out",
          isVisible && !isAnimatingOut 
            ? "opacity-100 scale-100 translate-y-0" 
            : "opacity-0 scale-95 translate-y-2",
          className
        )}
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
        }}
      >
        <Card className="relative max-w-sm shadow-2xl border-2 border-primary/20 bg-white dark:bg-gray-900">
          {/* Arrow pointing to target */}
          <div
            className={cn(
              "absolute w-4 h-4 bg-white dark:bg-gray-900 border rotate-45 border-primary/20",
              {
                'top-[-8px] left-1/2 transform -translate-x-1/2 border-b-0 border-r-0': tooltipDirection === 'top',
                'bottom-[-8px] left-1/2 transform -translate-x-1/2 border-t-0 border-l-0': tooltipDirection === 'bottom',
                'left-[-8px] top-1/2 transform -translate-y-1/2 border-t-0 border-r-0': tooltipDirection === 'left',
                'right-[-8px] top-1/2 transform -translate-y-1/2 border-b-0 border-l-0': tooltipDirection === 'right',
              }
            )}
          />

          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                  <Sparkles className="absolute -top-1 -right-1 h-3 w-3 text-yellow-400 animate-pulse" />
                </div>
                <h3 className="font-semibold text-lg">{currentStepData.title}</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              {currentStepData.description}
            </p>

            {/* Action hint */}
            {currentStepData.action && currentStepData.action.type !== 'none' && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  💡 {currentStepData.action.label || 
                    (currentStepData.action.type === 'click' ? 'Kliknite na označený prvok' : 'Prejdite myšou cez prvok')}
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Step indicator */}
                <div className="flex gap-1">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "w-2 h-2 rounded-full transition-all duration-200",
                        index === currentStep 
                          ? "bg-primary scale-125" 
                          : index < currentStep 
                            ? "bg-green-500" 
                            : "bg-gray-300 dark:bg-gray-600"
                      )}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground ml-2">
                  {currentStep + 1} z {steps.length}
                </span>
              </div>

              <div className="flex gap-2">
                {/* Previous button */}
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevious}
                    className="h-8"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Späť
                  </Button>
                )}

                {/* Skip button */}
                {currentStepData.skippable !== false && !isLastStep && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSkip}
                    className="h-8 text-muted-foreground hover:text-foreground"
                  >
                    Preskočiť
                  </Button>
                )}

                {/* Next/Finish button */}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="h-8 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white border-0"
                >
                  {isLastStep ? (
                    <>
                      Dokončiť
                      <Sparkles className="h-4 w-4 ml-1" />
                    </>
                  ) : (
                    <>
                      Ďalej
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// OnboardingTooltipRenderer removed - now handled directly in OnboardingProvider